import type { SensitiveApprovalNotification, SensitiveApprovalWebsocketPayload } from './types'
import { formatApprovalTarget as formatApprovalBusinessTarget } from './approvalBusiness'
import { SensitiveApprovalPhase, SensitiveApprovalStatus } from './types'

const SENSITIVE_APPROVAL_MESSAGE_TYPE = 'sensitive_approval'

interface WebsocketMessage {
  type?: string
  data?: unknown
}

interface SensitiveApprovalCurrentUser {
  corpUserId?: number | string | null
  merchantUserId?: number | string | null
  id?: number | string | null
  userId?: number | string | null
  corpUser?: {
    corpUserId?: number | string | null
  } | null
}

export function parseSensitiveApprovalMessage(message: WebsocketMessage): SensitiveApprovalWebsocketPayload | null {
  if (message.type !== SENSITIVE_APPROVAL_MESSAGE_TYPE) {
    return null
  }
  if (typeof message.data === 'string') {
    try {
      return JSON.parse(message.data) as SensitiveApprovalWebsocketPayload
    } catch (_error) {
      return null
    }
  }
  if (typeof message.data === 'object' && message.data !== null) {
    return message.data as SensitiveApprovalWebsocketPayload
  }
  return null
}

export function buildSensitiveApprovalNotification(
  payload: SensitiveApprovalWebsocketPayload,
): SensitiveApprovalNotification {
  if (payload.phase === SensitiveApprovalPhase.Pending || payload.status === SensitiveApprovalStatus.Pending) {
    return {
      title: '审核申请已提交',
      message: payload.message || formatApprovalTarget(payload),
      type: 'success',
    }
  }

  switch (payload.status) {
    case SensitiveApprovalStatus.Executed:
      return {
        title: '审核已通过并执行完成',
        message: payload.message || formatApprovalTarget(payload),
        type: 'success',
      }
    case SensitiveApprovalStatus.Rejected:
      return {
        title: '审核已拒绝',
        message: payload.message || formatApprovalTarget(payload),
        type: 'warning',
      }
    case SensitiveApprovalStatus.Canceled:
      return {
        title: '审核已取消',
        message: payload.message || formatApprovalTarget(payload),
        type: 'info',
      }
    case SensitiveApprovalStatus.Voided:
      return {
        title: '审核已作废',
        message: payload.message || formatApprovalTarget(payload),
        type: 'warning',
      }
    case SensitiveApprovalStatus.Expired:
      return {
        title: '审核已过期',
        message: payload.message || formatApprovalTarget(payload),
        type: 'warning',
      }
    case SensitiveApprovalStatus.ExecuteFailed:
      return {
        title: '审核执行失败',
        message: '审核已通过，但系统自动执行失败。请刷新页面确认业务状态；如仍未生效，请联系管理员处理或重新发起操作。',
        type: 'error',
      }
    default:
      return {
        title: '敏感操作审核状态更新',
        message: payload.message || formatApprovalTarget(payload),
        type: 'info',
      }
  }
}

export function shouldShowSensitiveApprovalNotification(payload: SensitiveApprovalWebsocketPayload) {
  return !(payload.phase === SensitiveApprovalPhase.Pending || payload.status === SensitiveApprovalStatus.Pending)
}

export function isSensitiveApprovalApplicant(
  payload: SensitiveApprovalWebsocketPayload,
  currentUser?: SensitiveApprovalCurrentUser | null,
) {
  if (!currentUser) {
    return false
  }
  return (
    isSameIdentity(payload.applicantCorpUserId, currentUser.corpUserId ?? currentUser.corpUser?.corpUserId) ||
    isSameIdentity(payload.applicantMerchantUserId, currentUser.merchantUserId ?? currentUser.id ?? currentUser.userId)
  )
}

export function createSensitiveApprovalPendingPromptTracker() {
  const promptedTicketIds = new Set<number>()

  return {
    shouldPrompt(payload: SensitiveApprovalWebsocketPayload, currentUser?: SensitiveApprovalCurrentUser | null) {
      if (payload.ticketId && payload.status && payload.status !== SensitiveApprovalStatus.Pending) {
        promptedTicketIds.delete(payload.ticketId)
      }
      if (
        !payload.ticketId ||
        !(payload.phase === SensitiveApprovalPhase.Pending || payload.status === SensitiveApprovalStatus.Pending)
      ) {
        return false
      }
      if (isSensitiveApprovalApplicant(payload, currentUser)) {
        return false
      }
      if (promptedTicketIds.has(payload.ticketId)) {
        return false
      }
      promptedTicketIds.add(payload.ticketId)
      return true
    },
  }
}

function isSameIdentity(left?: number | string | null, right?: number | string | null) {
  if (left === undefined || left === null || right === undefined || right === null) {
    return false
  }
  return String(left) === String(right)
}

function formatApprovalTarget(payload: SensitiveApprovalWebsocketPayload) {
  return formatApprovalBusinessTarget(payload, '审核状态已更新')
}
